import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standard inactivity window for a marketplace: 30 min total, warn at 25.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutes → force logout
const WARNING_MS      = 25 * 60 * 1000;   // 25 minutes → show warning
const COUNTDOWN_MS    = IDLE_TIMEOUT_MS - WARNING_MS; // 5-minute countdown
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

/**
 * Watches for user activity. If the user is idle for WARNING_MS, shows a
 * countdown dialog. If the user does not respond within COUNTDOWN_MS, logs
 * them out. Clicking "Stay Signed In" calls /auth/refresh to renew the token.
 */
export default function IdleTimeoutManager({ token, onLogout, onTokenRefresh }) {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(Math.ceil(COUNTDOWN_MS / 1000));
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownIntervalRef.current = null;
  }, []);

  const performLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (onLogout) onLogout();
    toast.info("You've been signed out due to inactivity.");
    navigate("/login", { replace: true });
  }, [clearAllTimers, navigate, onLogout]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(Math.ceil(COUNTDOWN_MS / 1000));
      // Start visible countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((c) => (c > 0 ? c - 1 : 0));
      }, 1000);
      // Hard logout timer
      logoutTimerRef.current = setTimeout(performLogout, COUNTDOWN_MS);
    }, WARNING_MS);
  }, [clearAllTimers, performLogout]);

  const handleStaySignedIn = async () => {
    try {
      const res = await fetch(`${API}/auth/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          localStorage.setItem("token", data.token);
          if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
          if (onTokenRefresh) onTokenRefresh(data.token, data.user);
          toast.success("Session extended.");
        }
        setShowWarning(false);
        startTimers();
      } else {
        // Refresh failed → session cannot be extended
        performLogout();
      }
    } catch (err) {
      console.error("Refresh failed:", err);
      performLogout();
    }
  };

  // Reset the warning timer on any user activity — but NOT while the warning
  // dialog is open (that would defeat the purpose of the countdown).
  useEffect(() => {
    if (!token) return;
    const handleActivity = () => {
      if (!showWarning) startTimers();
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    startTimers();
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      clearAllTimers();
    };
  }, [token, showWarning, startTimers, clearAllTimers]);

  if (!token) return null;

  const mm = Math.floor(countdown / 60).toString().padStart(2, "0");
  const ss = (countdown % 60).toString().padStart(2, "0");

  return (
    <Dialog open={showWarning} onOpenChange={() => { /* prevent ESC/backdrop close — force explicit choice */ }}>
      <DialogContent className="sm:max-w-md" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-testid="idle-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#FF6B6B]" />
            Are you still there?
          </DialogTitle>
          <DialogDescription className="pt-2">
            For your security, you&apos;ll be signed out in{" "}
            <span className="font-mono font-semibold text-foreground" data-testid="idle-timeout-countdown">
              {mm}:{ss}
            </span>{" "}
            due to inactivity.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={performLogout} data-testid="idle-timeout-signout-btn">
            Sign out now
          </Button>
          <Button onClick={handleStaySignedIn} data-testid="idle-timeout-stay-btn" className="bg-[#FF6B6B] hover:bg-[#FF6B6B]/90">
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
